import React from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  QrCode, CheckCircle2, XCircle, RepeatIcon, Camera, CameraOff, Keyboard,
} from "lucide-react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCANNER_ID = "eh-scanner-view";

export default function ScannerPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const [token, setToken] = React.useState("");
  const [result, setResult] = React.useState(null);
  const [authed, setAuthed] = React.useState(null);
  const [cameraOn, setCameraOn] = React.useState(false);
  const [cameraError, setCameraError] = React.useState(null);
  const scannerRef = React.useRef(null);
  const lastScanRef = React.useRef({ token: "", at: 0 });

  React.useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const doCheckin = React.useCallback(async (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    // de-dupe rapid duplicate scans (camera fires many times)
    const now = Date.now();
    if (lastScanRef.current.token === code && now - lastScanRef.current.at < 3000) return;
    lastScanRef.current = { token: code, at: now };
    try {
      const res = await axios.post(`${API}/checkin`, { qr_token: code });
      setResult(res.data);
      if (res.data.status === "ok") toast.success(t("scanner.ok"));
      else if (res.data.status === "duplicate") toast.warning(t("scanner.duplicate"));
    } catch (e) {
      if (e.response?.status === 404) { setResult({ status: "notfound", token: code }); toast.error(t("scanner.notfound")); }
      else toast.error(e.response?.data?.detail || "Check-in failed");
    }
    setToken("");
  }, [t]);

  const stopCamera = React.useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { if (s.isScanning) await s.stop(); } catch { /* ignore */ }
      try { await s.clear(); } catch { /* ignore */ }
    }
    setCameraOn(false);
  }, []);

  const startCamera = React.useCallback(async () => {
    setCameraError(null);
    try {
      const html5 = new Html5Qrcode(SCANNER_ID, {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.EAN_13,
        ],
        verbose: false,
      });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 160 }, aspectRatio: 1.0 },
        (decoded) => { doCheckin(decoded); },
        () => { /* per-frame failure, ignore */ },
      );
      setCameraOn(true);
    } catch (e) {
      setCameraError(e?.message || String(e));
      toast.error("Camera unavailable — grant permission or use manual input.");
      scannerRef.current = null;
      setCameraOn(false);
    }
  }, [doCheckin]);

  React.useEffect(() => () => { stopCamera(); }, [stopCamera]);

  if (authed === null) return <div className="p-16 text-center text-slate-400 font-mono-diplo text-xs">{t("common.loading")}</div>;
  if (authed === false) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-4">
        <div className="tag-mono mb-2">{t("nav.scanner")}</div>
        <h1 className="font-serif-diplo text-3xl font-bold text-[color:var(--navy)]">{t("scanner.title")}</h1>
        <div className="gold-underline mx-auto mt-4 mb-6" />
        <p className="text-sm text-slate-600 mb-4">Door staff — please sign in via the Admin page first.</p>
        <a href="/admin" data-testid="scanner-signin-link" className="btn-navy inline-block px-5 py-2.5 rounded-lg text-sm font-semibold">Sign in</a>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="tag-mono mb-2">{t("nav.scanner")}</div>
      <h1 className="font-serif-diplo text-3xl sm:text-4xl font-bold text-[color:var(--navy)]">{t("scanner.title")}</h1>
      <div className="gold-underline mt-4 mb-8" />

      <div className="diplomatic-card p-5 sm:p-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <QrCode className="w-4 h-4 text-[color:var(--forest)]" />
            <span className="tag-mono">Live camera · QR + Barcode</span>
          </div>
          {cameraOn ? (
            <button data-testid="scanner-camera-stop" onClick={stopCamera}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 text-rose-700 font-semibold">
              <CameraOff className="w-3.5 h-3.5" /> Stop camera
            </button>
          ) : (
            <button data-testid="scanner-camera-start" onClick={startCamera}
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[color:var(--forest)] text-white font-semibold hover:bg-[color:var(--forest-dark)]">
              <Camera className="w-3.5 h-3.5" /> Start camera
            </button>
          )}
        </div>

        <div
          id={SCANNER_ID}
          data-testid="scanner-camera-view"
          className={`relative rounded-xl overflow-hidden ${cameraOn ? "bg-black min-h-[260px]" : "bg-slate-100 min-h-[160px] flex items-center justify-center text-xs text-slate-500"}`}
        >
          {!cameraOn && (
            <div className="text-center p-6">
              <Camera className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              Tap "Start camera" and point at the delegate's QR or barcode.
              <br/>Allow camera access when prompted.
            </div>
          )}
        </div>

        {cameraError && (
          <div data-testid="scanner-camera-error" className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
            Camera error: {cameraError}
          </div>
        )}

        <div className="mt-5 pt-5 border-t border-[color:var(--line)]">
          <div className="flex items-center gap-2 mb-2">
            <Keyboard className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />
            <span className="tag-mono">Manual entry</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="scanner-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doCheckin(token)}
              placeholder={t("scanner.placeholder")}
              className="flex-1 border border-slate-200 rounded-lg px-4 py-3 text-sm font-mono-diplo focus:outline-none focus:border-[color:var(--forest)]"
            />
            <button data-testid="scanner-check-btn" onClick={() => doCheckin(token)}
              className="btn-navy px-5 py-3 rounded-lg text-sm font-semibold">
              {t("scanner.check")}
            </button>
          </div>
        </div>

        {result && (
          <div className="mt-6 p-4 rounded-xl border" data-testid="scanner-result">
            {result.status === "ok" && (
              <div className="flex items-start gap-3 text-emerald-700">
                <CheckCircle2 className="w-6 h-6 mt-0.5" />
                <div>
                  <div className="font-serif-diplo text-lg text-[color:var(--navy)]">{result.booking?.name}</div>
                  <div className="text-xs text-slate-600">{result.session?.title?.[lng] || result.session?.title?.en}</div>
                  <div className="tag-mono mt-1">{t("scanner.ok")}</div>
                </div>
              </div>
            )}
            {result.status === "duplicate" && (
              <div className="flex items-start gap-3 text-amber-700">
                <RepeatIcon className="w-6 h-6 mt-0.5" />
                <div>
                  <div className="font-serif-diplo text-lg text-[color:var(--navy)]">{result.booking?.name}</div>
                  <div className="text-xs text-slate-600">{result.session?.title?.[lng] || result.session?.title?.en}</div>
                  <div className="tag-mono mt-1">{t("scanner.duplicate")}</div>
                </div>
              </div>
            )}
            {result.status === "notfound" && (
              <div className="flex items-start gap-3 text-rose-700">
                <XCircle className="w-6 h-6 mt-0.5" />
                <div>
                  <div className="tag-mono">{t("scanner.notfound")}</div>
                  {result.token && <div className="text-[10px] font-mono text-rose-500 break-all mt-1">{result.token}</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
